// WhyAustraliaPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyAustraliaPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'Points-Based Immigration',
      description: 'Transparent SkillSelect system with clear pathways for skilled workers. Australia invites thousands of skilled migrants annually through various visa subclasses.'
    },
    {
      icon: Heart,
      title: 'Exceptional Quality of Life',
      description: 'World-class healthcare (Medicare), excellent education system, beautiful climate, and consistently ranked among the most liveable cities globally.'
    },
    {
      icon: Briefcase,
      title: 'Strong Economy & Job Market',
      description: 'Robust economy with high wages, low unemployment, and strong demand for skilled workers across multiple industries including IT, healthcare, and engineering.'
    },
    {
      icon: GraduationCap,
      title: 'World-Class Education',
      description: 'Access to top-ranked universities and free public education for PR holders\' children. Australian qualifications recognized worldwide.'
    },
    {
      icon: Shield,
      title: 'Comprehensive Benefits',
      description: 'Medicare healthcare coverage, social security benefits, and strong legal protections for workers and residents.'
    },
    {
      icon: TrendingUp,
      title: 'Path to Citizenship',
      description: 'Permanent residents can apply for Australian citizenship after 4 years of residence (including 12 months as PR).'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Why Choose Australia for Permanent Residency?
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Australia offers one of the world's most attractive skilled migration programs, combining economic opportunity with exceptional lifestyle benefits.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#202356] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>Did you know?</strong> Australia's permanent residents enjoy full work rights, access to Medicare, and can sponsor eligible family members. PR status is valid indefinitely as long as you meet residency requirements.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyAustraliaPR;
